import { MetricStat, UserPerformanceData } from './userPerformance.service';

// View-model consumed by the Overview cards, derived from the raw API response.

export type IndicatorUnit = 'count' | 'hours' | 'percent';

/**
 * Caret direction shown next to an indicator, reflecting the raw change:
 * - 'up'    -> value increased (current > previous)
 * - 'down'  -> value decreased (current < previous)
 * - 'none'  -> no comparison available, renders a neutral "-"
 */
export type CaretDirection = 'up' | 'down' | 'none';

/**
 * Whether the change is good or bad, driving the chip color independently of the
 * caret direction (e.g. more billable hours is good, more overdue tasks is bad).
 */
export type IndicatorTone = 'positive' | 'negative' | 'neutral';

export interface BillableHoursData {
  /** Team average billable share (0-100). */
  teamAveragePercent: number;
  /** Selected user billable share (0-100). */
  youPercent: number;
  /** Billable share in the previous period, for the footer indicator. */
  lastPeriodBillablePercent: number;
}

export interface ComparisonIndicator {
  key: string;
  /** Caption shown above the value, e.g. "Task overdue". */
  label: string;
  /** Explanatory text shown in the info-icon tooltip. */
  tooltip: string;
  /** Current value; formatting depends on `unit`. */
  value: number;
  unit: IndicatorUnit;
  /** Absolute change vs. the previous period (magnitude only); null when unavailable. */
  delta: number | null;
  /** Caret to render, reflecting the direction of change (current vs. previous). */
  caret: CaretDirection;
  /** Chip color, reflecting whether the change is good or bad for this metric. */
  tone: IndicatorTone;
  /** Team average for this metric, same unit as `value`. */
  teamAvg: number;
}

export interface OverviewViewModel {
  billableHours: BillableHoursData;
  indicators: ComparisonIndicator[];
}

/** Formats an indicator value according to its unit. */
export const formatIndicatorValue = (value: number, unit: IndicatorUnit): string => {
  if (unit === 'hours') return `${value}h`;
  if (unit === 'percent') return `${value}%`;
  return `${value}`;
};

const minutesToHours = (minutes: number): number => Math.floor((Number(minutes) || 0) / 60);

/**
 * Resolves the caret direction and chip tone from current vs. previous.
 *
 * The caret always follows the raw change (up = increased, down = decreased), while the
 * tone follows the metric's "good" direction (`higherIsBetter`):
 * - Task/Request overdue: lower is better, so a decrease is positive (green).
 * - My timelogs / Clock accuracy: higher is better, so an increase is positive (green).
 *
 * A missing delta (no previous period to compare against) or an unchanged value yields
 * a neutral comparison.
 */
const resolveComparison = (
  current: number,
  previous: number,
  delta: number | null | undefined,
  higherIsBetter: boolean,
): { caret: CaretDirection; tone: IndicatorTone } => {
  if (delta === null || delta === undefined || current === previous) {
    return { caret: 'none', tone: 'neutral' };
  }

  const increased = current > previous;
  const improved = higherIsBetter ? increased : !increased;

  return {
    caret: increased ? 'up' : 'down',
    tone: improved ? 'positive' : 'negative',
  };
};

/** Negative deltas are ignored (shown as magnitude); a missing delta stays null. */
const displayDelta = (delta: number | null | undefined): number | null =>
  delta === null || delta === undefined ? null : Math.abs(delta);

export const buildOverviewViewModel = (data: UserPerformanceData): OverviewViewModel => {
  const { billable_hours: billable, task_overdue: task, request_overdue: request, timelogs, clock_accuracy: clock } = data;

  const clockCurrent = clock.current_percentage ?? clock.currenct_percentage ?? 0;

  // Take the absolute value on minutes BEFORE converting, so a negative delta like
  // -32min becomes 32min -> 0h (not Math.floor(-0.53) = -1 -> abs -> 1h).
  const timelogsDeltaMinutes = displayDelta(timelogs.delta_minutes);
  const timelogsDeltaHours = timelogsDeltaMinutes === null ? null : minutesToHours(timelogsDeltaMinutes);
  const timelogsCurrentHours = minutesToHours(timelogs.current_minutes);
  const timelogsPreviousHours = minutesToHours(timelogs.previous_minutes);

  const countIndicator = (
    key: string,
    label: string,
    tooltip: string,
    stat: MetricStat,
    higherIsBetter: boolean,
  ): ComparisonIndicator => ({
    key,
    label,
    tooltip,
    value: stat.current,
    unit: 'count',
    delta: displayDelta(stat.delta),
    ...resolveComparison(stat.current, stat.previous, stat.delta, higherIsBetter),
    teamAvg: stat.team_average,
  });

  return {
    billableHours: {
      teamAveragePercent: billable.team_average.billable_percentage,
      youPercent: billable.current.billable_percentage,
      lastPeriodBillablePercent: billable.previous.billable_percentage,
    },
    indicators: [
      countIndicator('task-overdue', 'Task overdue', 'Tasks past their due date during the selected period.', task, false),
      countIndicator(
        'request-overdue',
        'Request overdue',
        'Requests past their due date during the selected period.',
        request,
        false,
      ),
      {
        key: 'my-timelogs',
        label: 'My timelogs',
        tooltip: 'Total hours you logged during the selected period.',
        value: timelogsCurrentHours,
        unit: 'hours',
        delta: timelogsDeltaHours,
        ...resolveComparison(timelogsCurrentHours, timelogsPreviousHours, timelogs.delta_minutes, true),
        teamAvg: minutesToHours(timelogs.team_average_minutes),
      },
      {
        key: 'clock-accuracy',
        label: 'Clock in/out accuracy',
        tooltip: 'How closely your clock in/out matches your scheduled hours.',
        value: clockCurrent,
        unit: 'percent',
        delta: displayDelta(clock.delta_percentage),
        ...resolveComparison(clockCurrent, clock.previous_percentage, clock.delta_percentage, true),
        teamAvg: clock.team_average_percentage,
      },
    ],
  };
};
